import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

interface SettingsProps {
  propelauth: any; // We'll type this properly later
}

interface ApiKey {
  id: string;
  key_id: string;
  key_id_display: string;
  name: string;
  last_used_at: string | null;
  created_at: string;
  is_active: boolean;
}

interface CreateApiKeyResponse {
  id: string;
  key_id: string;
  name: string;
  key_secret: string;
  message: string;
  usage_instructions: {
    header: string;
    python_client: string;
  };
}

export function Settings({ propelauth }: SettingsProps) {
  const [copied, setCopied] = useState(false);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<CreateApiKeyResponse | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);
  
  const pipCommand = "pip install --index-url https://app.runalphaloops.com/public/ alphaloop-experiments";
  
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const copySecret = async (secret: string) => {
    try {
      await navigator.clipboard.writeText(secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy secret: ', err);
    }
  };

  // Get user ID from PropelAuth
  const getUserId = async () => {
    try {
      const authInfo = await propelauth.getAuthenticationInfoOrNull();
      return authInfo?.user?.userId || null;
    } catch (error) {
      console.error('Error getting user ID:', error);
      return null;
    }
  };

  // Fetch API keys
  const fetchApiKeys = async () => {
    try {
      const userId = await getUserId();
      if (!userId) return;

      const response = await fetch('/api/api-keys-list', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setApiKeys(data.api_keys || []);
      } else {
        console.error('Failed to fetch API keys');
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  // Create new API key
  const createApiKey = async () => {
    if (!newKeyName.trim()) return;

    try {
      setCreating(true);
      const userId = await getUserId();
      if (!userId) return;

      const response = await fetch('/api/api-keys-create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
        body: JSON.stringify({ name: newKeyName.trim() }),
      });

      if (response.ok) {
        const data: CreateApiKeyResponse = await response.json();
        setCreatedKey(data);
        setNewKeyName('');
        // Refresh the list
        await fetchApiKeys();
      } else {
        const error = await response.json();
        alert(`Failed to create API key: ${error.error}`);
      }
    } catch (error) {
      console.error('Error creating API key:', error);
      alert('Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  // Delete API key
  const deleteApiKey = async (keyId: string, name: string) => {
    if (!confirm(`Are you sure you want to delete the API key "${name}"? This cannot be undone.`)) {
      return;
    }

    try {
      const userId = await getUserId();
      if (!userId) return;

      const response = await fetch('/api/api-keys-delete', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
        body: JSON.stringify({ keyId }),
      });

      if (response.ok) {
        // Refresh the list
        await fetchApiKeys();
      } else {
        const error = await response.json();
        alert(`Failed to delete API key: ${error.error}`);
      }
    } catch (error) {
      console.error('Error deleting API key:', error);
      alert('Failed to delete API key');
    }
  };

  // Load Prism.js for syntax highlighting
  useEffect(() => {
    // Add Prism CSS
    const prismCSS = document.createElement('link');
    prismCSS.rel = 'stylesheet';
    prismCSS.href = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/themes/prism-tomorrow.min.css';
    document.head.appendChild(prismCSS);

    // Add Prism JS
    const prismCore = document.createElement('script');
    prismCore.src = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/components/prism-core.min.js';
    prismCore.onload = () => {
      const prismAutoloader = document.createElement('script');
      prismAutoloader.src = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/plugins/autoloader/prism-autoloader.min.js';
      prismAutoloader.onload = () => {
        // Trigger highlighting
        if ((window as any).Prism) {
          (window as any).Prism.highlightAll();
        }
      };
      document.head.appendChild(prismAutoloader);
    };
    document.head.appendChild(prismCore);

    // Fetch API keys on component mount
    fetchApiKeys();

    // Cleanup
    return () => {
      document.head.removeChild(prismCSS);
      document.head.removeChild(prismCore);
    };
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="border-b border-border pb-4">
        <h1 className="text-3xl font-bold">⚙️ Settings</h1>
        <p className="text-muted-foreground">Manage your account, API keys, and integrations</p>
      </div>

      {/* Python Client Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            🐍 Python Client Library
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            Use the official AlphaLoop Python client to track experiments programmatically from your ML workflows.
          </p>
          
          {/* Installation Command */}
          <div className="space-y-2">
            <h4 className="font-medium">📦 Installation</h4>
            <div className="bg-muted p-3 rounded-md font-mono text-sm flex items-center justify-between">
              <code className="flex-1">{pipCommand}</code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(pipCommand)}
                className="ml-2"
              >
                {copied ? "✅ Copied!" : "📋 Copy"}
              </Button>
            </div>
          </div>

          {/* Download Links */}
          <div className="space-y-2">
            <h4 className="font-medium">📁 Downloads</h4>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" asChild>
                <a href="/public/packages/alphaloop-experiments/alphaloop_experiments-1.0.0-py3-none-any.whl" download>
                  📦 Download Wheel (.whl)
                </a>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <a href="/public/packages/alphaloop-experiments/alphaloop-experiments-1.0.0.tar.gz" download>
                  📄 Source Distribution (.tar.gz)
                </a>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <a href="/public/" target="_blank" rel="noopener noreferrer">
                  🔍 Browse Package Index
                </a>
              </Button>
            </div>
          </div>

          {/* Documentation */}
          <div className="space-y-2">
            <h4 className="font-medium">📖 Documentation & Examples</h4>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" asChild>
                <a href="/docs/python-client.html" target="_blank" rel="noopener noreferrer">
                  📚 Python Client Guide
                </a>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <a href="/docs/" target="_blank" rel="noopener noreferrer">
                  📋 All Documentation
                </a>
              </Button>
            </div>
          </div>

          {/* Quick Start with Syntax Highlighting */}
          <div className="space-y-2">
            <h4 className="font-medium">🚀 Quick Start</h4>
            <div className="bg-card border rounded-md overflow-hidden">
              <pre className="p-4 overflow-x-auto">
                <code className="language-python">{`from alphaloop_experiments import ExperimentsClient

# Initialize client (get API key from "API Keys" section below)
client = ExperimentsClient(api_key="ak_your_key_here")

# Create and run experiment
experiment = client.run_simple_experiment(
    name="My First Experiment",
    parameters={"model": "random_forest", "n_estimators": 100},
    result_data={"accuracy": 0.95, "f1_score": 0.93}
)

print(f"✅ Experiment created: {experiment.external_id}")`}</code>
              </pre>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* API Keys Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            🔑 API Keys
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            Manage your API keys for programmatic access to AlphaLoop services.
          </p>

          {/* API Key Creation Success Modal */}
          {createdKey && (
            <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 p-4 rounded-md space-y-4">
              <div className="flex items-start gap-2">
                <span className="text-green-600 dark:text-green-400 text-sm">✅</span>
                <div className="text-sm">
                  <p className="font-medium text-green-800 dark:text-green-200">API Key Created Successfully!</p>
                  <p className="text-green-700 dark:text-green-300 mt-1">{createdKey.message}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <h5 className="font-medium text-green-800 dark:text-green-200">Your API Key:</h5>
                <div className="bg-green-100 dark:bg-green-900 p-3 rounded border flex items-center justify-between">
                  <code className="font-mono text-sm break-all text-green-800 dark:text-green-200">
                    {createdKey.key_secret}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copySecret(createdKey.key_secret)}
                    className="ml-2 border-green-300 text-green-700 hover:bg-green-200"
                  >
                    {secretCopied ? "✅ Copied!" : "📋 Copy"}
                  </Button>
                </div>
                
                <div className="text-xs text-green-700 dark:text-green-300">
                  <p><strong>Usage:</strong> {createdKey.usage_instructions.python_client}</p>
                </div>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCreatedKey(null)}
                className="border-green-300 text-green-700 hover:bg-green-200"
              >
                Dismiss
              </Button>
            </div>
          )}

          {/* Create New API Key */}
          <div className="space-y-2">
            <h4 className="font-medium">Create New API Key</h4>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Enter API key name..."
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                className="flex-1 px-3 py-2 border border-border rounded-md bg-background text-foreground"
                disabled={creating}
              />
              <Button
                onClick={createApiKey}
                disabled={creating || !newKeyName.trim()}
              >
                {creating ? "Creating..." : "Create Key"}
              </Button>
            </div>
          </div>

          {/* API Keys List */}
          <div className="space-y-2">
            <h4 className="font-medium">Your API Keys</h4>
            {loading ? (
              <div className="text-center py-4">
                <p className="text-muted-foreground">Loading API keys...</p>
              </div>
            ) : apiKeys.length === 0 ? (
              <div className="text-center py-8 bg-muted rounded-md">
                <p className="text-muted-foreground">No API keys created yet.</p>
                <p className="text-sm text-muted-foreground mt-1">Create your first API key above to get started.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {apiKeys.map((key) => (
                  <div key={key.id} className="flex items-center justify-between p-3 bg-muted rounded-md">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{key.name}</span>
                        <code className="text-xs bg-background px-2 py-1 rounded">
                          {key.key_id_display}
                        </code>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Created: {formatDate(key.created_at)}
                        {key.last_used_at && (
                          <span className="ml-4">
                            Last used: {formatDate(key.last_used_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteApiKey(key.key_id, key.name)}
                    >
                      Delete
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* General Settings */}
      <Card>
        <CardHeader>
          <CardTitle>⚙️ General Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            Additional settings and preferences will be available here.
          </p>
        </CardContent>
      </Card>
    </div>
  );
} 